// src/modules/DailyHuddle.tsx
import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, CheckCircle, Calendar, Users, FileText, X } from 'lucide-react'; // ← added X
import { Incident, ComplianceTask, ActionStatus, IncidentStatus } from '../types/compliance';

interface DailyHuddleProps {
  incidents: Incident[];
  tasks: ComplianceTask[];
  onClose: () => void;
}

export function DailyHuddle({ incidents, tasks, onClose }: DailyHuddleProps) {
  const overdueTasks = tasks.filter(t => t.status !== ActionStatus.COMPLETED && new Date(t.dueDate) < new Date());
  const openIncidents = incidents.filter(i => i.status !== IncidentStatus.CLOSED);
  const today = new Date().toISOString().slice(0,10);
  const tasksDueToday = tasks.filter(t => t.dueDate.slice(0,10) === today);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-[#123B36]">📋 Daily Huddle Agenda</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-red-500"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div className="bg-red-50 p-3 rounded-lg border border-red-200">
            <h4 className="font-bold text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Overdue Actions ({overdueTasks.length})</h4>
            {overdueTasks.length === 0 ? <p className="text-sm text-gray-500">None – great work!</p> : (
              <ul className="list-disc list-inside text-sm">
                {overdueTasks.map(t => <li key={t.id}>{t.title} (due {new Date(t.dueDate).toLocaleDateString()})</li>)}
              </ul>
            )}
          </div>

          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <h4 className="font-bold text-yellow-700 flex items-center gap-2"><Clock className="w-4 h-4" /> Due Today ({tasksDueToday.length})</h4>
            {tasksDueToday.length === 0 ? <p className="text-sm text-gray-500">None for today.</p> : (
              <ul className="list-disc list-inside text-sm">
                {tasksDueToday.map(t => <li key={t.id}>{t.title} – {t.assignedOwner}</li>)}
              </ul>
            )}
          </div>

          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="font-bold text-blue-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Open Incidents ({openIncidents.length})</h4>
            {openIncidents.length === 0 ? <p className="text-sm text-gray-500">No open incidents.</p> : (
              <ul className="list-disc list-inside text-sm">
                {openIncidents.map(i => <li key={i.id}>{i.incidentRef} – {i.status} ({i.resident?.identityCode})</li>)}
              </ul>
            )}
          </div>

          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <p className="text-sm text-gray-600">📌 Review all tasks and incidents in the Compliance Dashboard.</p>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="bg-[#123B36] text-white px-4 py-2 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}